import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Windows 95 Notepad predates Format. Commands are presentation-only: no
// editor, clipboard or filesystem actions are performed by these menus.
export const NOTEPAD_MENUS = [
    { name: '&File', items: [['&New', 'Ctrl+N'], ['&Open...', 'Ctrl+O'], ['&Save', 'Ctrl+S'], ['Save &As...'], null, ['Page Set&up...'], ['&Print', 'Ctrl+P'], null, ['E&xit']] },
    { name: '&Edit', items: [['&Undo', 'Ctrl+Z'], null, ['Cu&t', 'Ctrl+X'], ['&Copy', 'Ctrl+C'], ['&Paste', 'Ctrl+V'], ['De&lete', 'Del'], null, ['Select &All', 'Ctrl+A'], ['Time/&Date', 'F5'], null, ['&Word Wrap', '', true]] },
    { name: '&Search', items: [['&Find...'], ['Find &Next', 'F3']] },
    { name: '&Help', items: [['&Help Topics'], null, ['&About Notepad']] },
];
export const EXPLORER_MENUS = [
    { name: '&File', items: [['&Open'], ['&Properties'], null, ['&Close']] },
    { name: '&Edit', items: [['&Undo', 'Ctrl+Z'], null, ['Cu&t', 'Ctrl+X'], ['&Copy', 'Ctrl+C'], ['&Paste', 'Ctrl+V'], null, ['Select &All', 'Ctrl+A'], ['&Invert Selection']] },
    { name: '&Help', items: [['&Help Topics'], null, ['&About Windows']] },
];
const Bar = styled.div`
    display: flex; flex-shrink: 0; position: relative; z-index: 2;
    padding: 1px 0; border-bottom: 1px solid #808080; box-shadow: 0 1px #fff; user-select: none;
    button { font: inherit; color: black; border: 0; border-radius: 0; cursor: default; }
`;
const Trigger = styled.button`
    background: ${({ $active }) => $active ? '#000080' : 'transparent'};
    color: ${({ $active }) => $active ? 'white' : 'black'} !important;
    padding: 2px 6px;
    &:focus-visible { outline: 1px dotted black; outline-offset: -2px; }
`;
const Popup = styled.div`
    position: absolute; top: 100%; left: 0; min-width: 190px; padding: 3px;
    background: #c0c0c0;
    box-shadow: inset 1px 1px #fff, inset -1px -1px #000, inset 2px 2px #dfdfdf, inset -2px -2px #808080;
    button { display: grid; grid-template-columns: 18px 1fr auto; gap: 8px; width: 100%; text-align: left;
        padding: 3px 12px 3px 2px; white-space: nowrap; background: transparent; }
    button:hover, button:focus { background: #000080; color: white; outline: none; }
    [role=separator] { border-top: 1px solid #808080; border-bottom: 1px solid white; margin: 3px 1px; }
`;
function Label({ text }) {
    const i = text.indexOf('&');
    return i < 0 ? text : <>{text.slice(0, i)}<u>{text[i + 1]}</u>{text.slice(i + 2)}</>;
}
export default function ClassicMenu({ menus }) {
    const [open, setOpen] = useState(-1);
    const ref = useRef(null);
    const triggers = useRef([]);
    useEffect(() => {
        const dismiss = e => { if (!ref.current?.contains(e.target)) setOpen(-1); };
        document.addEventListener('pointerdown', dismiss);
        return () => document.removeEventListener('pointerdown', dismiss);
    }, []);
    const close = () => { setOpen(-1); triggers.current[open]?.focus(); };
    const keyDown = e => {
        if (e.key === 'Escape' && open >= 0) { e.preventDefault(); e.stopPropagation(); close(); }
        if (e.key === 'Tab') setOpen(-1);
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const current = open >= 0 ? open : triggers.current.indexOf(document.activeElement);
            const next = (current + (e.key === 'ArrowRight' ? 1 : -1) + menus.length) % menus.length;
            if (open >= 0) setOpen(next);
            triggers.current[next]?.focus();
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const index = triggers.current.indexOf(document.activeElement);
            if (index >= 0) {
                setOpen(index);
                requestAnimationFrame(() => ref.current?.querySelector('[role=menu] button')?.focus());
            } else {
                const entries = [...ref.current.querySelectorAll('[role=menu] button')];
                const next = (entries.indexOf(document.activeElement) + (e.key === 'ArrowDown' ? 1 : -1) + entries.length) % entries.length;
                entries[next]?.focus();
            }
        }
    };
    return <Bar ref={ref} role="menubar" aria-label="Window menu" onKeyDown={keyDown}>
        {menus.map(({ name, items }, index) => <div key={name} style={{ position: 'relative' }}>
            <Trigger ref={el => { triggers.current[index] = el; }} role="menuitem" aria-haspopup="menu" aria-label={name.replace('&', '')}
                aria-expanded={open === index} $active={open === index}
                onClick={() => setOpen(open === index ? -1 : index)}
                onPointerEnter={() => { if (open >= 0) setOpen(index); }}><Label text={name} /></Trigger>
            {open === index && <Popup role="menu" aria-label={name.replace('&', '')}>
                {items.map((item, i) => item ? <button key={i} type="button" role={item[2] ? 'menuitemcheckbox' : 'menuitem'}
                    aria-label={item[0].replace('&', '')} aria-checked={item[2] ? true : undefined} onClick={close}>
                    <span aria-hidden="true">{item[2] ? '✓' : ''}</span><span><Label text={item[0]} /></span><span>{item[1]}</span>
                </button> : <div key={i} role="separator" />)}
            </Popup>}
        </div>)}
    </Bar>;
}
